import type { ChartsRadialAxisProps, ScaleName, PolarAxisConfig } from '../../../../models/axis';
import { type PolarChartSeriesType } from '../../../../models/seriesType/config';
import {
  type PolarExtremumGetter,
  type ChartSeriesConfig,
  type PolarExtremumGetterResult,
} from '../../corePlugins/useChartSeriesConfig';
import { type ProcessedSeries } from '../../corePlugins/useChartSeries/useChartSeries.types';
import { isPolarSeriesType } from '../../../isPolar';

const axisExtremumCallback = <SeriesType extends PolarChartSeriesType>(
  acc: PolarExtremumGetterResult,
  chartType: SeriesType,
  axis: PolarAxisConfig<ScaleName, any, ChartsRadialAxisProps>,
  axisDirection: 'rotation' | 'radius',
  seriesConfig: ChartSeriesConfig<SeriesType>,
  axisIndex: number,
  formattedSeries: ProcessedSeries<SeriesType>,
): PolarExtremumGetterResult => {
  const getter =
    axisDirection === 'rotation'
      ? seriesConfig[chartType].rotationExtremumGetter
      : seriesConfig[chartType].radiusExtremumGetter;
  const series = formattedSeries[chartType]?.series ?? {};

  const [minChartTypeData, maxChartTypeData] = (getter as PolarExtremumGetter<SeriesType>)?.({
    series,
    axis,
    axisIndex,
    isDefaultAxis: axisIndex === 0,
  }) ?? [Infinity, -Infinity];

  const [minData, maxData] = acc;

  return [Math.min(minChartTypeData, minData), Math.max(maxChartTypeData, maxData)];
};

export const getAxisExtremum = <SeriesType extends PolarChartSeriesType>(
  axis: PolarAxisConfig<ScaleName, any, ChartsRadialAxisProps>,
  axisDirection: 'rotation' | 'radius',
  seriesConfig: ChartSeriesConfig<SeriesType>,
  axisIndex: number,
  formattedSeries: ProcessedSeries<SeriesType>,
) => {
  const polarSeriesTypes = Object.keys(seriesConfig).filter(isPolarSeriesType);

  const extremums = polarSeriesTypes.reduce<PolarExtremumGetterResult>(
    (acc, charType) =>
      axisExtremumCallback(
        acc,
        charType as SeriesType,
        axis,
        axisDirection,
        seriesConfig,
        axisIndex,
        formattedSeries,
      ),
    [Infinity, -Infinity],
  );

  if (Number.isNaN(extremums[0]) || Number.isNaN(extremums[1])) {
    return [Infinity, -Infinity];
  }

  return extremums;
};
